import * as React from 'react';
import { useState } from 'react';

import { Button } from '@shared/components/bootstrap';

import { Panel } from '@admin/components/helpers/panel';
import { Spinner } from '@admin/components/spinner';
import { Timestamp } from '@admin/components/timestamp';
import { client } from '@admin/libraries/apollo';
import { Dispatch__TaskKindEnum, useDispatchTaskCancelMutation, useDispatchTasksQuery } from '@admin/schema';

const DEFAULT_COLLAPSED = false;

type Task = NonNullable<ReturnType<typeof useDispatchTasksQuery>['data']>['dispatchTasks'][0];

const TaskRow: React.FC<{
  task: Task;
  onCancel: (id: string) => void;
  canceling: boolean;
}> = ({ task, onCancel, canceling }) => {
  const isRunning = !task.completedAt && !task.canceledAt;
  const buttonText = task.isClaimer ? 'Unlock' : 'Cancel';

  return (
    <tr>
      <td className="text-left">{task.kind === Dispatch__TaskKindEnum.Inbound ? 'Inbound' : 'Outbound'}</td>
      <td className="text-left">{task.isClaimer ? 'Claimer' : 'Teammate'}</td>
      <td className="text-left">{task.user.name}</td>
      <td className="text-left">{task.user.hrsTitle}</td>
      <td className="text-left">{task.serialCode || ''}</td>
      <td className="text-left">
        <Timestamp value={task.createdAt} timezone={task.timeZone} />
      </td>
      <td className="text-left">
        {task.completedAt && <Timestamp value={task.completedAt} timezone={task.timeZone} />}
      </td>
      <td className="text-left">{task.canceledAt && <Timestamp value={task.canceledAt} timezone={task.timeZone} />}</td>
      <td className="text-left">{task.canceler?.name || ''}</td>
      <td className="col-sm-2">
        {isRunning && (
          <Button kind="danger" onClick={() => onCancel(task.id)} disabled={canceling}>
            {buttonText}
          </Button>
        )}
      </td>
    </tr>
  );
};

export const WarehouseClaimersPanel: React.FC<{ dispatchID: string }> = ({ dispatchID }) => {
  const { data, loading, refetch } = useDispatchTasksQuery({ client, variables: { dispatchID } });
  const [dispatchTaskCancel] = useDispatchTaskCancelMutation({ client });
  const [collapsed, setCollapsed] = useState<boolean>(DEFAULT_COLLAPSED);
  const [canceling, setCanceling] = useState(false);

  const handleCancel = async (taskID: string) => {
    const confirmed = window.confirm('Are you sure you want to cancel this dispatch task?');
    if (!confirmed) return;

    setCanceling(true);
    try {
      const result = await dispatchTaskCancel({
        variables: {
          id: taskID,
        },
      });

      if (result.data?.dispatchTaskCancel.status === 'OK') {
        await refetch();
      } else {
        alert(result.data?.dispatchTaskCancel.error || 'Failed to cancel task');
      }
    } catch (error) {
      alert('An error occurred while canceling the task');
      console.error(error);
    } finally {
      setCanceling(false);
    }
  };

  if (loading || !data) {
    return <Spinner />;
  }

  return (
    <Panel>
      <Panel.Header>
        <Panel.Collapse collapsed={collapsed} onChange={setCollapsed} />
        <Panel.Title>Warehouse Claimers and Teammates</Panel.Title>
      </Panel.Header>
      <Panel.Body>
        <table className="table table-striped" id="warehouse-claimers">
          <thead>
            <tr>
              <th className="text-left">Task Kind</th>
              <th className="text-left">Role</th>
              <th className="text-left">Name</th>
              <th className="text-left">Title</th>
              <th className="text-left">Serial Code</th>
              <th className="text-left">Started</th>
              <th className="text-left">Completed</th>
              <th className="text-left">Unlocked</th>
              <th className="text-left">Unlocked By</th>
              <th className="col-sm-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.dispatchTasks.map((task) => (
              <TaskRow key={task.id} task={task} onCancel={handleCancel} canceling={canceling} />
            ))}
          </tbody>
        </table>
      </Panel.Body>
    </Panel>
  );
};
